import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request, type Message, type MessagesResponse, type SentMessage } from './client';

export type { Message, MessagesResponse };

/**
 * Replies, internal notes and owners on one submission.
 *
 * The thread is the API's (`GET/POST /submissions/{id}/messages`, 047), typed
 * from its schema: receipts the form sent, replies sent from the mailbox that
 * owns the record's stream, and staff-only notes. A reply is refused while a
 * template's `[prompt]` is still in it (422), to a person who asked us to stop
 * except on that request (409), and from a role that cannot see the address
 * (403). Subject, address and words come back null where the caller's view of
 * the record is redacted.
 *
 * The owner is the API's too (053): `PATCH /submissions/{id}/owner`
 * (submissions:update) takes `{ ownerId: string | null }`, any active account
 * or none, and every row the API sends carries `ownerId` / `ownerName`. A 404
 * from it means the record is gone or outside the caller's studies, and a 422
 * that the person chosen has no active account; the control shows either
 * sentence as the API wrote it.
 *
 * Deliberately no inbound half: what the person writes back lands in the lab
 * inbox, not here. Threading replies in would need a mail webhook and a
 * matching rule, and neither belongs in a first cut.
 */

export type MessageKind = Message['kind'];
export type Delivery = NonNullable<Message['delivery']>;

/** What staff write: a reply or a note. Receipts are the form's own. */
export type SendMessageBody = { kind: 'reply'; subject: string; body: string } | { kind: 'note'; body: string };

export const useMessages = (id: string | null) =>
  useQuery({
    queryKey: ['messages', id],
    queryFn: () => request<MessagesResponse>(`/submissions/${id}/messages`),
    enabled: id !== null,
  });

function useConversationMutation<B>(path: (id: string) => string, method: 'POST' | 'PATCH') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: B }) => request<never>(path(id), { method, body }),
    onSuccess: (_data, { id }) => {
      // A reply or a note changes this thread; an owner change shows on the
      // row; both may move the submission's updatedAt and the Overview counts.
      void qc.invalidateQueries({ queryKey: ['messages', id] });
      void qc.invalidateQueries({ queryKey: ['submission', id] });
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SendMessageBody }) =>
      request<SentMessage>(`/submissions/${id}/messages`, { method: 'POST', body }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['messages', id] });
      void qc.invalidateQueries({ queryKey: ['submission', id] });
      void qc.invalidateQueries({ queryKey: ['submissions'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export const useSetOwner = () =>
  useConversationMutation<{ ownerId: string | null }>((id) => `/submissions/${id}/owner`, 'PATCH');

/**
 * A collection route the API does not have: an older deployment's 404.
 *
 * No longer used here — the owner route exists, so its 404 is a record out
 * of reach, not a missing endpoint. Kept only because access.ts and
 * endorsements.ts re-export it for their own Preview states; it belongs in
 * those files.
 */
export const isEndpointMissing = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 404;
